import React, { useState, useEffect, useRef } from 'react';
import { userAPI, groupAPI } from '../services/api';
import { getDiceBearGroupAvatar } from '../services/dicebear';
import { apiErrorMessage } from '../utils/apiError';

const Groups = ({ onClose, onGroupClick, isEmbedded = false }) => {
  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    is_public: true,
    is_restricted: false,
    is_readonly: false
  });

  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadGroups();
  }, []);

  const CACHE_KEY = 'groups_overview_cache_v1';
  const CACHE_TTL_MS = 30_000; // 30 seconds to coalesce duplicate loads

  const loadGroups = async () => {
    try {
      setLoading(true);
      // Try cache first
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached && cached.t && Date.now() - cached.t < CACHE_TTL_MS) {
            setMyGroups(cached.my_groups || []);
            setPublicGroups(cached.public_groups || []);
            return; // Use cached data; skip network
          }
        }
      } catch (_) {}

      const overview = await userAPI.getGroupsOverview();
      const my = overview.data.my_groups || [];
      const pub = overview.data.public_groups || [];
      setMyGroups(my);
      setPublicGroups(pub);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), my_groups: my, public_groups: pub }));
      } catch (_) {}
    } catch (err) {
      setError('Failed to load groups');
      console.error('Error loading groups:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      const response = await groupAPI.createGroup(createForm);
      setShowCreateDialog(false);
      setCreateForm({ name: '', description: '', is_public: true, is_restricted: false, is_readonly: false });
      try { sessionStorage.removeItem(CACHE_KEY); } catch (_) {}
      await loadGroups();
      // Navigate to the new group
      onGroupClick(response.data.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create group'));
      console.error(err);
    }
  };

  const getGroupAvatarUrl = (groupName) => {
    return getDiceBearGroupAvatar(groupName);
  };

  const renderGroupCard = (group, isMyGroup = false) => (
    <div 
      key={group.id} 
      className="group-card"
      style={{ cursor: 'pointer' }}
      onClick={() => onGroupClick(group.id)}
    >
      <img 
        src={getGroupAvatarUrl(group.name)} 
        alt={`${group.name} avatar`}
        className="group-avatar"
      />
      <div className="group-info">
        <div className="group-name">
          {group.name}
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          Owner: {group.owner.username}
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          Members: {group.member_count}
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          Boards: {group.board_count ?? 0}
        </div>
        {!group.is_public && (
          <div style={{ color: '#ffc107', fontSize: '12px', fontWeight: 'bold' }}>
            Private Group
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    if (isEmbedded) {
      return <div>Loading groups...</div>;
    }
    return (
      <div className="groups-modal">
        <div className="groups-content">
          <div className="groups-header">
            <h2>Groups</h2>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>
          <div className="groups-body">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const groupsContent = (
    <>
      {error && <div className="error-message">{error}</div>}
      
      <div className="groups-section">
        <h3>My Groups</h3>
        {myGroups.length === 0 ? (
          <div className="no-groups">You haven't joined any groups yet</div>
        ) : (
          <div className="groups-grid">
            {myGroups.map(group => renderGroupCard(group, true))}
          </div>
        )}
      </div>

      <div className="groups-section">
        <h3>Public Groups</h3>
        {publicGroups.length === 0 ? (
          <div className="no-groups">No public groups available</div>
        ) : (
          <div className="groups-grid">
            {publicGroups.map(group => renderGroupCard(group, false))}
          </div>
        )}
      </div>
    </>
  );

  if (isEmbedded) {
    return groupsContent;
  }

  return (
    <div className="groups-modal">
      <div className="groups-content">
        <div className="groups-header">
          <h2>Groups</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => {
                setError('');
                setShowCreateDialog(true);
              }}
              className="btn"
              style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
            >
              Add Group
            </button>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>
        </div>
        <div className="groups-body">
          {groupsContent}
        </div>
      </div>

      {/* Create Group Dialog */}
      {showCreateDialog && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-card" style={{ maxWidth: '400px', width: '100%', textAlign: 'left' }}>
            <h3 className="modal-title" style={{ textAlign: 'left' }}>Create New Group</h3>
            {error && <div className="error-message">{error}</div>}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Group Name (max 24 characters):
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                maxLength={24}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Description (optional, max 64 characters):
              </label>
              <input
                type="text"
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                maxLength={64}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_public}
                  onChange={(e) => setCreateForm({...createForm, is_public: e.target.checked})}
                />
                Public Group
              </label>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_restricted}
                  onChange={(e) => setCreateForm({...createForm, is_restricted: e.target.checked})}
                />
                Restricted Group
              </label>
              <div style={{ 
                fontSize: '12px', 
                fontStyle: 'italic', 
                color: 'var(--color-text-muted)', 
                marginLeft: '24px',
                marginTop: '4px'
              }}>
                Only the owner is allowed to invite users for restricted groups.
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_readonly}
                  onChange={(e) => setCreateForm({...createForm, is_readonly: e.target.checked})}
                />
                Read-only Group
              </label>
              <div style={{ 
                fontSize: '12px', 
                fontStyle: 'italic', 
                color: 'var(--color-text-muted)', 
                marginLeft: '24px',
                marginTop: '4px'
              }}>
                Only the owner can create new stacks for read-only groups.
              </div>
            </div>
            <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowCreateDialog(false)}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: '#fff' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateGroup}
                className="btn"
                style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-on-success)' }}
                disabled={!createForm.name.trim()}
              >
                Add Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups; 